/**
 * Who is on a programme's team, and how much each builder is carrying.
 *
 * These derivations sat inline in a 948-line component, recomputed on every
 * render and testable only by rendering the whole screen.
 *
 * @module components/teams/team-assignments
 */

import { responsibilityLabel, roleLabel } from "@/components/shared/role-labels";
import type { SessionContext, UserSummary } from "@/platform";
import type { ModuleOwnershipAssignment, ProgrammeResponsibilityAssignment } from "@/server/types";

/** People as picker options, each hinted with their role as labelled everywhere else. */
export function userOptions(users: readonly UserSummary[]) {
  return users.map((user) => ({
    value: user.id,
    label: user.displayName,
    hint: roleLabel(user.accountRole),
  }));
}

/** One person, with every responsibility they hold on this programme. */
export interface ResponsibilityGroup {
  userId: string;
  displayName: string;
  accountRole: string;
  assignments: ProgrammeResponsibilityAssignment[];
}

/** How many modules one builder owns on this programme. */
export interface BuilderLoad {
  userId: string;
  displayName: string;
  moduleCount: number;
  /** The account is deactivated, so this load needs moving rather than adding to. */
  inactive: boolean;
}

/**
 * One comparator for every list on this screen.
 *
 * `numeric` so "Programme 10" follows "Programme 2" rather than preceding it,
 * and `sensitivity` so case and accents do not split a run of names. Three
 * copies of this had grown up and two of them disagreed.
 *
 * @param left - The first title or name.
 * @param right - The second.
 * @returns The sort order.
 */
export function compareText(left: string, right: string): number {
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Group a programme's responsibilities by the person holding them.
 *
 * People sort by name and their responsibilities by label, so the table reads
 * the same way twice running whatever order the API answered in.
 *
 * @param responsibilities - Every assignment on the programme.
 * @param users - The tenant's users, for names the assignment does not carry.
 * @returns One group per person, sorted.
 */
export function groupResponsibilities(
  responsibilities: readonly ProgrammeResponsibilityAssignment[],
  users: readonly UserSummary[],
): ResponsibilityGroup[] {
  const usersById = new Map(users.map((user) => [user.id, user]));
  const groups = new Map<string, ResponsibilityGroup>();

  responsibilities.forEach((assignment) => {
    const existing = groups.get(assignment.userId);
    if (existing) {
      existing.assignments.push(assignment);
      return;
    }
    const user = usersById.get(assignment.userId);
    groups.set(assignment.userId, {
      userId: assignment.userId,
      // An assignment naming a user the tenant list does not have is still
      // real: showing its id beats dropping the row.
      displayName: user?.displayName ?? assignment.userId,
      accountRole: user?.accountRole ?? "Unknown account role",
      assignments: [assignment],
    });
  });

  return [...groups.values()]
    .map((group) => ({
      ...group,
      assignments: [...group.assignments].sort((left, right) =>
        compareText(
          responsibilityLabel(left.responsibility),
          responsibilityLabel(right.responsibility),
        ),
      ),
    }))
    .sort((left, right) => compareText(left.displayName, right.displayName));
}

/** One row per module, naming whoever builds it. */
export function moduleOwnerRows(
  modules: readonly { id: string; title: string; code: string }[],
  owners: readonly ModuleOwnershipAssignment[],
  users: readonly UserSummary[],
): {
  id: string;
  title: string;
  code: string;
  ownership: ModuleOwnershipAssignment | null;
  ownerName: string | null;
}[] {
  const ownerByModule = new Map(owners.map((owner) => [owner.moduleId, owner]));
  const nameById = new Map(users.map((user) => [user.id, user.displayName]));
  return modules.map((module) => {
    const ownership = ownerByModule.get(module.id) ?? null;
    return {
      id: module.id,
      title: module.title,
      code: module.code,
      ownership,
      ownerName: ownership ? (nameById.get(ownership.userId) ?? null) : null,
    };
  });
}

/**
 * How many modules each builder owns.
 *
 * Includes a builder who owns nothing yet, because a zero is the number a lead
 * is looking for when deciding who to assign next.
 *
 * @param owners - Active module ownerships on the programme.
 * @param responsibilities - Every assignment, for the builders among them.
 * @param users - The tenant's users, for names and to exclude inactive ones.
 * @returns One entry per builder, heaviest first.
 */
export function builderLoads(
  owners: readonly ModuleOwnershipAssignment[],
  responsibilities: readonly ProgrammeResponsibilityAssignment[],
  users: readonly UserSummary[],
): BuilderLoad[] {
  const counts = new Map<string, number>();
  owners.forEach((owner) => {
    counts.set(owner.userId, (counts.get(owner.userId) ?? 0) + 1);
  });

  const builders = new Set(
    responsibilities
      .filter((assignment) => assignment.responsibility === "builder")
      .map((assignment) => assignment.userId),
  );

  return users
    .filter(
      (user) =>
        // A deactivated account holding modules is still holding them, and the
        // module table names it as the owner: leaving it out here put the same
        // person on the programme in one panel and gone from the other.
        (user.status === "active" && (builders.has(user.id) || counts.has(user.id))) ||
        counts.has(user.id),
    )
    .map((user) => ({
      userId: user.id,
      displayName: user.displayName,
      moduleCount: counts.get(user.id) ?? 0,
      inactive: user.status !== "active",
    }))
    .sort(
      (left, right) =>
        right.moduleCount - left.moduleCount || compareText(left.displayName, right.displayName),
    );
}

/**
 * Whether revoking this lead would leave the programme without one.
 *
 * A programme with no lead has nobody accountable for its workflow, so the
 * last one cannot be removed without naming a replacement first.
 *
 * @param assignment - The assignment being considered for revocation.
 * @param responsibilities - Every assignment on the programme.
 * @returns `true` when this is the only lead.
 */
export function isSoleLead(
  assignment: ProgrammeResponsibilityAssignment,
  responsibilities: readonly ProgrammeResponsibilityAssignment[],
): boolean {
  if (assignment.responsibility !== "lead") {
    return false;
  }
  return responsibilities.filter((entry) => entry.responsibility === "lead").length <= 1;
}

/** Why a permission box is refused, or what pressing it would do. */
export interface PermissionRefusal {
  /** The programme is historical: nothing on it can be changed. */
  locked: boolean;
  /** The tenant reserves this permission for an administrator. */
  manageable: boolean;
  /** Removing it would leave the programme with no lead. */
  soleLead: boolean;
  /** A change is in flight, so the board is briefly frozen. */
  saving: boolean;
  /** Whether the person already holds it. */
  assigned: boolean;
  /** The permission's own name, e.g. "Viewer". */
  label: string;
  /** Why this account cannot manage the team at all, when that is the cause. */
  permittedReason?: string;
}

/**
 * The one sentence a permission box carries, refused or not.
 *
 * Every cause of a refusal names itself. Two of the four used to fall through
 * to the enabled wording, so a box on a locked programme announced "Add
 * Viewer" and then refused the press.
 *
 * @param refusal - What is true of this box.
 * @returns The reason, or the action it offers.
 */
export function permissionReason(refusal: PermissionRefusal): string {
  if (refusal.locked) {
    return "This is a locked historical programme, so its team is read only";
  }
  if (!refusal.manageable) {
    return refusal.permittedReason ?? "This permission is managed by Client Admin";
  }
  if (refusal.soleLead) {
    return "Assign another Programme Lead before removing this permission";
  }
  if (refusal.saving) {
    return "Wait for the change in progress to finish";
  }
  return `${refusal.assigned ? "Remove" : "Add"} ${refusal.label}`;
}

/** Why a team write refuses, or `null` when it does not. */
export function writeRefusal(state: {
  locked: boolean;
  saving: boolean;
  permitted: boolean;
  permittedReason?: string;
  ready: boolean;
  readyReason: string;
}): string | null {
  if (state.locked) {
    return "This programme is locked, so its team cannot change.";
  }
  if (!state.permitted) {
    return state.permittedReason ?? "Your role cannot change this programme's team.";
  }
  if (state.saving) {
    return "A change is already in progress.";
  }
  return state.ready ? null : state.readyReason;
}

/** Why an account that may not manage the team sees it without its controls. */
export const READ_ONLY_TEAM_REASON =
  "Only this programme's Lead or a Client Admin can change its team.";

/** Why anyone but a Client Admin sees a team read-only while leads may not manage it. */
export const LEAD_TEAM_MANAGEMENT_OFF_REASON =
  "Lead team management is turned off in Provider Settings, so only a Client Admin can change this team.";

/**
 * Why this account cannot change the team, or null when it can. With the
 * tenant setting off it is the setting that bars everybody but a Client
 * Admin, so naming the Lead as someone who could is false; and a Client Admin
 * is never told the setting is what bars them.
 */
export function teamRefusal(
  session: SessionContext,
  allowLeadTeamManagement: boolean,
  readOnly: boolean,
): string | null {
  const { accountRole } = session.subject;
  const isTenantAdmin = accountRole === "client-admin" || accountRole === "platform-admin";
  if (!readOnly && (isTenantAdmin || allowLeadTeamManagement)) {
    return null;
  }
  return !isTenantAdmin && !allowLeadTeamManagement
    ? LEAD_TEAM_MANAGEMENT_OFF_REASON
    : READ_ONLY_TEAM_REASON;
}

/** Why removing a module's builder refuses. */
export function revokeRefusal(readOnlyReason: string | null, locked: boolean): string {
  if (locked) {
    return "This is a locked historical programme, so its team is read only";
  }
  return readOnlyReason ?? "Wait for the change in progress to finish";
}

/** Why a Draft programme refuses a module builder, named once for both readers. */
export const DRAFT_BUILDER_REASON =
  "A programme still in Draft cannot have module builders assigned yet.";

/** Why the module picker refuses: the programme's state, or nothing to pick. */
export function modulePickerRefusal(blocked: boolean, moduleCount: number): string | undefined {
  if (blocked) {
    return DRAFT_BUILDER_REASON;
  }
  return moduleCount ? undefined : "This programme has no modules to assign yet.";
}

/** Why the builder picker refuses: the state, no module chosen, or nobody to choose. */
export function builderPickerRefusal(
  blocked: boolean,
  moduleId: string,
  candidateCount: number,
): string | undefined {
  if (blocked) {
    return DRAFT_BUILDER_REASON;
  }
  if (!moduleId) {
    return "Choose a module first.";
  }
  return candidateCount ? undefined : "Nobody on this team can take a module yet.";
}
